/**
 * Vimsamsha Guruvadi D20 Calculation Logic
 * Based on 20 Ayurvedic Gunas (fundamental qualities)
 *
 * Structure:
 * - 20 parts per sign (each part = 1°30')
 * - Each part corresponds to one of 20 Guruvadi Gunas
 * - Formula: Part_number = floor(degrees_in_sign / 1.5) + 1
 */

import { devWarn } from '../../../../../utils/devLog';
import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number;
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * Dosha types (Ayurvedic constitution)
 */
export type DoshaType = 'Vata' | 'Pitta' | 'Kapha';

/**
 * Vimsamsha division with Guna attributes
 */
export interface VimsamshaDivision {
  partNumber: number;
  degreeStart: number;
  degreeEnd: number;
  guna: string;           // Latin transliteration
  sanskrit: string;       // Sanskrit original
  meaning: string;        // English translation
  primaryDosha: DoshaType;
  secondaryDosha?: DoshaType;
}

/**
 * 20 Guruvadi Gunas with degrees and doshas
 */
export const VIMSAMSHA_GUNAS: VimsamshaDivision[] = [
  {
    partNumber: 1,
    degreeStart: 0.0,
    degreeEnd: 1.5,
    guna: 'Guru',
    sanskrit: 'गुरु',
    meaning: 'Heavy',
    primaryDosha: 'Kapha'
  },
  {
    partNumber: 2,
    degreeStart: 1.5,
    degreeEnd: 3.0,
    guna: 'Laghu',
    sanskrit: 'लघु',
    meaning: 'Light',
    primaryDosha: 'Vata',
    secondaryDosha: 'Pitta'
  },
  {
    partNumber: 3,
    degreeStart: 3.0,
    degreeEnd: 4.5,
    guna: 'Manda',
    sanskrit: 'मन्द',
    meaning: 'Dull',
    primaryDosha: 'Kapha'
  },
  {
    partNumber: 4,
    degreeStart: 4.5,
    degreeEnd: 6.0,
    guna: 'Tikshna',
    sanskrit: 'तीक्ष्ण',
    meaning: 'Sharp',
    primaryDosha: 'Pitta',
    secondaryDosha: 'Vata'
  },
  {
    partNumber: 5,
    degreeStart: 6.0,
    degreeEnd: 7.5,
    guna: 'Shita',
    sanskrit: 'शीत',
    meaning: 'Cold',
    primaryDosha: 'Vata',
    secondaryDosha: 'Kapha'
  },
  {
    partNumber: 6,
    degreeStart: 7.5,
    degreeEnd: 9.0,
    guna: 'Ushna',
    sanskrit: 'उष्ण',
    meaning: 'Hot',
    primaryDosha: 'Pitta'
  },
  {
    partNumber: 7,
    degreeStart: 9.0,
    degreeEnd: 10.5,
    guna: 'Snigdha',
    sanskrit: 'स्निग्ध',
    meaning: 'Oily',
    primaryDosha: 'Pitta',
    secondaryDosha: 'Kapha'
  },
  {
    partNumber: 8,
    degreeStart: 10.5,
    degreeEnd: 12.0,
    guna: 'Ruksha',
    sanskrit: 'रूक्ष',
    meaning: 'Dry',
    primaryDosha: 'Vata'
  },
  {
    partNumber: 9,
    degreeStart: 12.0,
    degreeEnd: 13.5,
    guna: 'Shlakshna',
    sanskrit: 'श्लक्ष्ण',
    meaning: 'Smooth',
    primaryDosha: 'Kapha',
    secondaryDosha: 'Pitta'
  },
  {
    partNumber: 10,
    degreeStart: 13.5,
    degreeEnd: 15.0,
    guna: 'Khara',
    sanskrit: 'खर',
    meaning: 'Rough',
    primaryDosha: 'Vata'
  },
  {
    partNumber: 11,
    degreeStart: 15.0,
    degreeEnd: 16.5,
    guna: 'Sandra',
    sanskrit: 'सान्द्र',
    meaning: 'Thick',
    primaryDosha: 'Kapha'
  },
  {
    partNumber: 12,
    degreeStart: 16.5,
    degreeEnd: 18.0,
    guna: 'Drava',
    sanskrit: 'द्रव',
    meaning: 'Liquid',
    primaryDosha: 'Pitta',
    secondaryDosha: 'Vata'
  },
  {
    partNumber: 13,
    degreeStart: 18.0,
    degreeEnd: 19.5,
    guna: 'Mridu',
    sanskrit: 'मृदु',
    meaning: 'Soft',
    primaryDosha: 'Kapha',
    secondaryDosha: 'Pitta'
  },
  {
    partNumber: 14,
    degreeStart: 19.5,
    degreeEnd: 21.0,
    guna: 'Kathina',
    sanskrit: 'कठिन',
    meaning: 'Hard',
    primaryDosha: 'Vata'
  },
  {
    partNumber: 15,
    degreeStart: 21.0,
    degreeEnd: 22.5,
    guna: 'Sthira',
    sanskrit: 'स्थिर',
    meaning: 'Stable',
    primaryDosha: 'Kapha'
  },
  {
    partNumber: 16,
    degreeStart: 22.5,
    degreeEnd: 24.0,
    guna: 'Chala',
    sanskrit: 'चल',
    meaning: 'Mobile',
    primaryDosha: 'Vata',
    secondaryDosha: 'Pitta'
  },
  {
    partNumber: 17,
    degreeStart: 24.0,
    degreeEnd: 25.5,
    guna: 'Sukshma',
    sanskrit: 'सूक्ष्म',
    meaning: 'Subtle',
    primaryDosha: 'Vata',
    secondaryDosha: 'Pitta'
  },
  {
    partNumber: 18,
    degreeStart: 25.5,
    degreeEnd: 27.0,
    guna: 'Sthula',
    sanskrit: 'स्थूल',
    meaning: 'Gross',
    primaryDosha: 'Kapha'
  },
  {
    partNumber: 19,
    degreeStart: 27.0,
    degreeEnd: 28.5,
    guna: 'Pichchhila',
    sanskrit: 'पिच्छिल',
    meaning: 'Cloudy',
    primaryDosha: 'Kapha'
  },
  {
    partNumber: 20,
    degreeStart: 28.5,
    degreeEnd: 30.0,
    guna: 'Vishada',
    sanskrit: 'विशद',
    meaning: 'Clear',
    primaryDosha: 'Vata',
    secondaryDosha: 'Pitta'
  }
];

/**
 * Get Vimsamsha division by part number
 */
export const getVimsamshaByPartNumber = (partNumber: number): VimsamshaDivision | undefined => {
  return VIMSAMSHA_GUNAS.find(g => g.partNumber === partNumber);
};

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Calculate Vimsamsha part number from degrees within a sign (0-30°)
 * Formula: floor(degrees / 1.5) + 1
 */
export const calculateVimsamshaPart = (degreesInSign: number): number => {
  if (degreesInSign < 0 || degreesInSign >= 30) {
    devWarn(`Invalid degrees in sign: ${degreesInSign}. Must be 0-30.`);
    return 1;
  }

  const partNumber = Math.floor(degreesInSign / 1.5) + 1;
  return Math.min(Math.max(partNumber, 1), 20); // Clamp to 1-20
};

/**
 * Vimsamsha attributes for a planet
 */
export interface VimsamshaAttributes {
  planet: PlanetDisplay;
  partNumber: number;
  guna: string;
  sanskrit: string;
  meaning: string;
  primaryDosha: DoshaType;
  secondaryDosha?: DoshaType;
  degreesInSign: number;
}

/**
 * Calculate Vimsamsha attributes for a planet
 */
const calculatePlanetVimsamsha = (
  planet: GrahaPosition,
  isLagna: boolean = false,
  isHora: boolean = false,
  isChandra: boolean = false
): VimsamshaAttributes => {
  // Extract longitude from position string
  const totalLongitude = parseLongitudeFromPosition(planet.position || '');
  const degreesInSign = totalLongitude % 30;

  // Calculate part number
  const partNumber = calculateVimsamshaPart(degreesInSign);

  // Get Guna details
  const division = getVimsamshaByPartNumber(partNumber);

  if (!division) {
    devWarn(`No division found for part ${partNumber}`);
    return {
      planet: {
        name: planet.name,
        short_name: planet.short_name,
        longitude: totalLongitude,
        is_retrograde: planet.is_retrograde || false,
        is_lagna: isLagna,
        is_hora: isHora,
        is_chandra: isChandra
      },
      partNumber: 1,
      guna: 'Unknown',
      sanskrit: '',
      meaning: '',
      primaryDosha: 'Vata',
      degreesInSign
    };
  }

  return {
    planet: {
      name: planet.name,
      short_name: planet.short_name,
      longitude: totalLongitude,
      is_retrograde: planet.is_retrograde || false,
      is_lagna: isLagna,
      is_hora: isHora,
      is_chandra: isChandra
    },
    partNumber: division.partNumber,
    guna: division.guna,
    sanskrit: division.sanskrit,
    meaning: division.meaning,
    primaryDosha: division.primaryDosha,
    secondaryDosha: division.secondaryDosha,
    degreesInSign
  };
};

/**
 * Extract and calculate Vimsamsha divisions for all planets
 */
export interface VimsamshaData {
  divisions: VimsamshaAttributes[];
}

export const calculateVimsamshaDivisions = (apiResponse: JatakaResponse): VimsamshaData => {
  const divisions: VimsamshaAttributes[] = [];

  if (!apiResponse.graha_positions?.planets) {
    return { divisions };
  }

  // Get current Hora lord if available
  const horaLord = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Process all planets (includes Lagna)
  apiResponse.graha_positions.planets.forEach(planet => {
    const isLagna = planet.short_name === 'Lg';
    const isHora = planet.short_name === horaLord;
    const isChandra = planet.short_name === 'Ch';

    divisions.push(calculatePlanetVimsamsha(
      planet,
      isLagna,
      isHora,
      isChandra
    ));
  });

  return { divisions };
};
